/**
 * Resolution class ground truth (frontend, ESM)
 *
 * Single source of truth for the statutory class (board / ordinary / special) a
 * resolution must be drafted as. src/new-ui/data/resolutions.js re-exports
 * getResolutionKind from here, so ResolutionModal and every other caller keep
 * their existing import.
 *
 * Mirror of backend/new-ui-generation/shared/resolutionKinds.js — kept identical
 * by hand because frontend (ESM, consumed by CRA) and backend (CJS) cannot share
 * a single file, exactly as with companyContract.js / agmDeadlineEngine.js.
 * If you add or change an id, update BOTH files.
 */

/* ── SHARED LOGIC — BYTE-IDENTICAL MIRROR ─────────────────────────────────
 * Everything from here down to the export statement is duplicated VERBATIM in
 * the other copy. Only the header comment above and the export form below may
 * differ (module.exports vs export). The parity suite
 * (backend/new-ui-generation/shared/__tests__/resolutionKindsParity.test.js)
 * compares this region byte-for-byte and fails the moment the two drift.
 * ---------------------------------------------------------------------- */

// The three statutory classes a draft can be asked to satisfy. 'board' is the
// PERMISSIVE default on purpose: a missed inference must never block a
// legitimate draft, it only means the hard constraint isn't asserted.
const RESOLUTION_KINDS = ['board', 'ordinary', 'special'];

/**
 * Catalog id → statutory class. GROUND TRUTH read by both draft paths:
 *   - modal: src/new-ui/data/resolutions.js → getResolutionKind(item)
 *   - chat:  the draft_resolution tool's `catalogId` argument
 *
 * The card SUBTITLE remains the origin of each value ("u/s 196 · Special Res ·
 * MR-1" → special); kindFromSubtitle below is the parser, this table is the
 * transport that gets the answer to a backend which cannot see the catalog. The
 * parity suite re-derives the table from the live catalog, so a subtitle edit
 * that changes a class fails the build instead of silently disagreeing.
 *
 * An id ABSENT from this table is 'board'. Two absences are deliberate:
 *   intercorporate_loan — "u/s 186 · Special Res if >limit"
 *   rpt                 — "u/s 188 · Ordinary/Special Res"
 * Their class turns on facts the catalog does not hold (the amount against the
 * company's paid-up capital / net worth, whether the transaction is at arm's
 * length), so asserting one would print a guess as a statutory fact. The user's
 * own topic wording can still raise them, via the topic/reference inference in
 * resolution-drafts/contracts.js.
 */
const RESOLUTION_KIND_BY_ID = {
  // Directors & Key Managerial Personnel
  appt_independent_dir: 'special', // u/s 149(6) · Special Res · MGT-14
  appt_md: 'special', // u/s 196 · Special Res · MR-1
  removal_director: 'ordinary', // u/s 169 · Ordinary Resolution · Special Notice
  loan_director: 'special', // u/s 185 · Special Res · MGT-14
  // Share Capital & Securities
  increase_auth_cap: 'ordinary', // u/s 61 · Ordinary Res · SH-7
  private_placement: 'special', // u/s 42 · Special Res · PAS-3, PAS-4
  bonus_shares: 'special', // u/s 63 · Special Res · PAS-3
  buyback: 'special', // u/s 68 · Special Res · SH-8, SH-9
  sweat_equity: 'special', // u/s 54 · Special Res · SH-8
  esop: 'special', // u/s 62(1)(b) · Special Res · MGT-14
  // Banking, Borrowing & Finance
  corporate_guarantee: 'special', // u/s 186 · Special Res · MGT-14
  // Corporate Changes & Restructuring
  change_name: 'special', // u/s 13 · Special Res · INC-24, INC-25
  change_office_state: 'special', // u/s 13 · Special Res · INC-23 · CLB
  moa_alteration: 'special', // u/s 13 · Special Res · MGT-14
  aoa_alteration: 'special', // u/s 14 · Special Res · MGT-14
  merger: 'special', // u/s 230-232 · Special Res · NCLT
  demerger: 'special', // u/s 230-232 · Special Res · NCLT
  // Accounts, Audit & Dividend
  adopt_accounts: 'ordinary', // u/s 129 · Ordinary Res · AOC-4
  appt_sub_auditor: 'ordinary', // u/s 139(1) · Ordinary Res · ADT-1
  final_dividend: 'ordinary', // u/s 123 · Ordinary Resolution · AGM
};

/**
 * The ids whose class the catalog FIXES — i.e. the only ids worth naming to a
 * model, since every other id resolves to the permissive default anyway. The
 * chat tool builds its `catalogId` description from this list so the two can
 * never fall out of step.
 */
const CLASS_BOUND_RESOLUTION_IDS = Object.keys(RESOLUTION_KIND_BY_ID);

/**
 * Subtitle → class. Returns '' when the subtitle states no class, or states a
 * CONDITIONAL one ("Special Res if >limit", "Ordinary/Special Res") — the
 * threshold depends on facts the catalog doesn't know, so those must not be
 * asserted as a hard constraint.
 */
function kindFromSubtitle(subtitle = '') {
  const s = String(subtitle || '');
  if (!s) return '';
  // Conditional / either-or wording — no hard constraint.
  if (/(ordinary\s*\/\s*special|special\s*\/\s*ordinary)/i.test(s)) return '';
  if (/special\s*res\w*\s+(if|where|when|subject)\b/i.test(s)) return '';
  if (/\bspecial\s*res\w*\b/i.test(s)) return 'special';
  if (/\bordinary\s*res\w*\b/i.test(s)) return 'ordinary';
  return '';
}

/**
 * Catalog id → class, or '' when the id is unknown or its class is conditional.
 * '' means "no hard constraint" — NOT "board" — so callers can tell the two
 * apart and fall back to their own inference.
 */
function resolutionKindForId(id) {
  const key = String(id == null ? '' : id).trim();
  if (!key) return '';
  return RESOLUTION_KIND_BY_ID[key] || '';
}

/**
 * The statutory class a resolution must be drafted as.
 * @param {object} item  { id, subtitle, kind? } — a catalog item, or any object
 *                       carrying at least an id (History rows, chat handoffs).
 * @returns {'board'|'ordinary'|'special'}
 */
function resolveResolutionKind(item = {}) {
  const src = item && typeof item === 'object' ? item : {};
  const explicit = String(src.kind == null ? '' : src.kind).trim().toLowerCase();
  if (RESOLUTION_KINDS.includes(explicit)) return explicit;
  // A live catalog item carries its subtitle; a History row or a chat handoff
  // may carry only the id, so the table backs it up.
  return kindFromSubtitle(src.subtitle) || resolutionKindForId(src.id) || 'board';
}

export {
  RESOLUTION_KINDS,
  RESOLUTION_KIND_BY_ID,
  CLASS_BOUND_RESOLUTION_IDS,
  kindFromSubtitle,
  resolutionKindForId,
  resolveResolutionKind,
};
